import { z } from "zod";

/** The status page's words and pure logic (M6 round six, task 5; brief "The status page"; review B10). Lives in shared so packages/shared's
 *  vitest runs it and the Cloudflare Worker (`deploy/status/worker.ts`) bundles the very same code: the probe outside our server reads
 *  `/health` and `/health/status` every five minutes, turns them into one reading, keeps the last days of readings in Cloudflare's store,
 *  and the page renders from that store at request time (a probe that stopped shows "unknown" after fifteen minutes instead of a frozen
 *  "up"). The founder's line is a note in the same store. */
export const STATUS_COMPONENTS = ["app", "mailIn", "mailOut", "ai"] as const;
export type StatusComponent = (typeof STATUS_COMPONENTS)[number];
export const COMPONENT_STATES = ["up", "degraded", "down", "unknown"] as const;
export type ComponentState = (typeof COMPONENT_STATES)[number];
/** the brief's fifteen minutes: a newest check older than this reads "unknown" */
export const STATUS_STALE_MS = 15 * 60_000;
/** mail waiting longer than this (in the queue, or a reply past its send time) reads "degraded" */
export const STATUS_LAG_MS = 15 * 60_000;
export const STATUS_HISTORY_DAYS = 7;

const Iso = z.string().datetime({ offset: true });
const IsoOrNull = Iso.nullable();
/** what GET /health/status answers: times, one count and one outcome; nothing that names a workspace, a person or a text */
export const StatusFactsSchema = z.object({
  at: Iso,
  worker: z.object({ at: IsoOrNull }).strict(),
  inbound: z.object({ checkedAt: IsoOrNull, oldestSentAt: IsoOrNull, deadLetters: z.number().int().min(0).nullable(), lastMailAt: IsoOrNull }).strict(),
  outbound: z.object({ lastSentAt: IsoOrNull, oldestDueAt: IsoOrNull }).strict(),
  ai: z.object({ lastCallAt: IsoOrNull, lastOutcome: z.enum(["ok", "error", "budget", "lost"]).nullable() }).strict(),
}).strict();
export type StatusFacts = z.infer<typeof StatusFactsSchema>;
const State = z.enum(COMPONENT_STATES);
export const StatusReadingSchema = z.object({ at: Iso, app: State, mailIn: State, mailOut: State, ai: State }).strict();
export type StatusReading = z.infer<typeof StatusReadingSchema>;
export const StatusHistorySchema = z.array(StatusReadingSchema);
export const StatusNoteSchema = z.object({ text: z.string().min(1).max(500), at: Iso }).strict();
export type StatusNote = z.infer<typeof StatusNoteSchema>;

export type ProbeAnswer = { status: number; body: unknown } | null;
const ms = (iso: string | null | undefined) => (iso ? Date.parse(iso) : NaN);
/** one probe's reading from the two routes (null = unreachable or timed out): the app is up only on a 200 with `ok: true`; the facts
 *  unusable (null, not 200, not a StatusFacts) leave mail and the AI unknown; a dead worker (`checks.worker === false`) is down for all
 *  three; else the ages are taken on the facts' own clock (`facts.at`), so a skewed probe clock changes nothing */
export function readingFrom(at: Date, health: ProbeAnswer, facts: ProbeAnswer): StatusReading {
  const h = health && health.status === 200 && typeof health.body === "object" && health.body !== null ? (health.body as { ok?: unknown; checks?: { worker?: unknown } }) : null;
  const app: ComponentState = h?.ok === true ? "up" : "down";
  const parsed = facts && facts.status === 200 ? StatusFactsSchema.safeParse(facts.body) : null;
  if (!parsed?.success) return { at: at.toISOString(), app, mailIn: "unknown", mailOut: "unknown", ai: "unknown" };
  if (h && h.checks?.worker === false) return { at: at.toISOString(), app, mailIn: "down", mailOut: "down", ai: "down" };
  const f = parsed.data; const now = ms(f.at);
  const checked = ms(f.inbound.checkedAt);
  let mailIn: ComponentState;
  if (Number.isNaN(checked) || now - checked > STATUS_LAG_MS) mailIn = "down";
  else if ((f.inbound.oldestSentAt && checked - ms(f.inbound.oldestSentAt) > STATUS_LAG_MS) || (f.inbound.deadLetters ?? 0) > 0) mailIn = "degraded";
  else mailIn = "up";
  const mailOut: ComponentState = f.outbound.oldestDueAt && now - ms(f.outbound.oldestDueAt) > STATUS_LAG_MS ? "degraded" : "up";
  const ai: ComponentState = f.ai.lastOutcome === "error" || f.ai.lastOutcome === "lost" ? "degraded" : "up";
  return { at: at.toISOString(), app, mailIn, mailOut, ai };
}
/** the history with the reading appended, ascending by time, without readings older than the history's days before the new one */
export function appendReading(history: StatusReading[], reading: StatusReading): StatusReading[] {
  const floor = ms(reading.at) - STATUS_HISTORY_DAYS * 86_400_000;
  return [...history, reading].filter((r) => ms(r.at) >= floor).sort((a, b) => ms(a.at) - ms(b.at));
}
export const newestReading = (history: StatusReading[]): StatusReading | null => history.reduce<StatusReading | null>((best, r) => (!best || ms(r.at) > ms(best.at) ? r : best), null);
const allUnknown = (): Record<StatusComponent, ComponentState> => ({ app: "unknown", mailIn: "unknown", mailOut: "unknown", ai: "unknown" });
/** the newest reading's states, or all unknown when there is none or it is older than fifteen minutes (exactly fifteen still counts) */
export function currentStates(history: StatusReading[], now: Date): Record<StatusComponent, ComponentState> {
  const r = newestReading(history);
  if (!r || now.getTime() - ms(r.at) > STATUS_STALE_MS) return allUnknown();
  return { app: r.app, mailIn: r.mailIn, mailOut: r.mailOut, ai: r.ai };
}
export const STATE_RANK: Record<ComponentState, number> = { unknown: 0, up: 1, degraded: 2, down: 3 };
export const dayOf = (t: number) => new Date(t).toISOString().slice(0, 10);
/** one cell a UTC day, oldest first, the last one today: the day's worst known state; a day with no known reading is unknown */
export function dailyStates(history: StatusReading[], component: StatusComponent, now: Date, days = STATUS_HISTORY_DAYS): { day: string; state: ComponentState }[] {
  const worst = new Map<string, ComponentState>();
  for (const r of history) { const d = dayOf(ms(r.at)); const s = r[component]; if (s === "unknown") continue; const w = worst.get(d); if (!w || STATE_RANK[s] > STATE_RANK[w]) worst.set(d, s); }
  const out: { day: string; state: ComponentState }[] = [];
  for (let i = days - 1; i >= 0; i--) { const d = dayOf(now.getTime() - i * 86_400_000); out.push({ day: d, state: worst.get(d) ?? "unknown" }); }
  return out;
}
/** the store's history: [] for nothing, broken JSON or a non-array; invalid entries dropped */
export function parseHistory(text: string | null): StatusReading[] {
  if (!text) return [];
  let v: unknown; try { v = JSON.parse(text); } catch { return []; }
  if (!Array.isArray(v)) return [];
  return v.flatMap((x) => { const p = StatusReadingSchema.safeParse(x); return p.success ? [p.data] : []; });
}
/** the store's note: null for nothing or anything that is not a note */
export function parseNote(text: string | null): StatusNote | null {
  if (!text) return null;
  let v: unknown; try { v = JSON.parse(text); } catch { return null; }
  const p = StatusNoteSchema.safeParse(v); return p.success ? p.data : null;
}

/* ---------- the long view (2026-10-02): ninety days, one summary a day ---------- */
/** the page shows this many days, one bar each */
export const STATUS_PAGE_DAYS = 90;
/** a day's readings per component, counted by state: [up, degraded, down]; unknown readings are not counted */
const Counts = z.tuple([z.number().int().min(0), z.number().int().min(0), z.number().int().min(0)]);
export const StatusDaySchema = z.object({ app: Counts, mailIn: Counts, mailOut: Counts, ai: Counts }).strict();
export type StatusDay = z.infer<typeof StatusDaySchema>;
export type StatusDays = Record<string, StatusDay>;
const SLOT: Record<ComponentState, number> = { up: 0, degraded: 1, down: 2, unknown: -1 };
/** the readings counted per UTC day */
export function summariseDays(history: StatusReading[]): StatusDays {
  const out: StatusDays = {};
  for (const r of history) {
    const d = (out[dayOf(Date.parse(r.at))] ??= { app: [0, 0, 0], mailIn: [0, 0, 0], mailOut: [0, 0, 0], ai: [0, 0, 0] });
    for (const c of STATUS_COMPONENTS) { const slot = SLOT[r[c]]; if (slot >= 0) d[c][slot]! += 1; }
  }
  return out;
}
/** the stored summaries brought up to date from the readings kept (a week of them): today and yesterday are always recounted (the
 *  readings hold both whole), an older day only when it has no summary yet (the first run), since the week's oldest day is cut off
 *  and would count too little; days older than the page shows are dropped */
export function mergeDays(stored: StatusDays, history: StatusReading[], now: Date): StatusDays {
  const fresh = summariseDays(history);
  const recent = new Set([dayOf(now.getTime()), dayOf(now.getTime() - 86_400_000)]);
  const floor = dayOf(now.getTime() - (STATUS_PAGE_DAYS - 1) * 86_400_000);
  const out: StatusDays = {};
  for (const [day, sum] of Object.entries(stored)) if (day >= floor) out[day] = sum;
  for (const [day, sum] of Object.entries(fresh)) if (day >= floor && (recent.has(day) || !out[day])) out[day] = sum;
  return out;
}
/** the summaries worth saving this run, or null when nothing but today would change (2026-10-02: Cloudflare's free plan allows 1 000
 *  writes a day for the whole account, which a second product's status page shares). The page recounts today and yesterday from the
 *  readings at request time, so the store needs a day only once it is over: the first run after midnight UTC saves (yesterday is whole in
 *  the readings by then), a save that failed is made up on the next run, and a day without readings changes nothing */
export function daysToSave(stored: StatusDays, history: StatusReading[], now: Date): StatusDays | null {
  const merged = mergeDays(stored, history, now);
  const today = dayOf(now.getTime());
  const settled = (days: StatusDays) => Object.keys(days).filter((d) => d !== today).sort().map((d) => `${d}:${STATUS_COMPONENTS.map((c) => days[d]![c].join(",")).join("|")}`).join(";");
  return settled(merged) === settled(stored) ? null : merged;
}
/** the store's summaries: {} for nothing, broken JSON or a non-object; invalid days dropped */
export function parseDays(text: string | null): StatusDays {
  if (!text) return {};
  let v: unknown; try { v = JSON.parse(text); } catch { return {}; }
  if (!v || typeof v !== "object" || Array.isArray(v)) return {};
  const out: StatusDays = {};
  for (const [day, sum] of Object.entries(v as Record<string, unknown>)) { const p = StatusDaySchema.safeParse(sum); if (/^\d{4}-\d{2}-\d{2}$/.test(day) && p.success) out[day] = p.data; }
  return out;
}

export * from "./statusPage";
